// What can be uploaded. Temporarily (since 2026-11-04) attachments everywhere — task attachments, chat
// files, files on a space — must be images of at most 1 MB. The server (/api/upload) enforces it by
// reading the file's bytes; the web app checks first so people get a clear message. To lift it, set
// IMAGES_ONLY to false and raise MAX_UPLOAD_BYTES (the server's ATRIUM_MAX_UPLOAD_MB cap still applies).

export const IMAGES_ONLY = true;
export const MAX_UPLOAD_BYTES = 1024 * 1024;

/** Image types that can be attached: they show inline in every browser. */
export const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/avif'];

/** For a file picker's accept attribute: only offers images while they're all that can be attached. */
export const UPLOAD_ACCEPT = IMAGES_ONLY ? IMAGE_TYPES.join(',') : undefined;

const MB = 1024 * 1024;
export const sizeLabel = (bytes: number) => (bytes >= MB ? `${(bytes / MB).toFixed(1).replace(/\.0$/, '')} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

const LIMIT_TEXT = `${IMAGES_ONLY ? 'images (PNG, JPEG, GIF, WebP or AVIF)' : 'files'} up to ${sizeLabel(MAX_UPLOAD_BYTES)}`;
export const UPLOAD_LIMIT_TEXT = `For now you can attach ${LIMIT_TEXT}.`;

/** Why this file can't be uploaded, or null when it can. */
export function uploadProblem(file: { name: string; type: string; size: number }): string | null {
  if (IMAGES_ONLY && !IMAGE_TYPES.includes(file.type.toLowerCase())) return `${file.name} isn't an image. ${UPLOAD_LIMIT_TEXT}`;
  if (file.size > MAX_UPLOAD_BYTES) return `${file.name} is ${sizeLabel(file.size)}. ${UPLOAD_LIMIT_TEXT}`;
  return null;
}

/** The image type a file's first bytes show, or null if they aren't one of IMAGE_TYPES. */
export function sniffImageType(b: Uint8Array): string | null {
  const ascii = (from: number, to: number) => String.fromCharCode(...b.subarray(from, to));
  if (b.length >= 8 && b[0] === 0x89 && ascii(1, 4) === 'PNG' && b[4] === 0x0d && b[5] === 0x0a && b[6] === 0x1a && b[7] === 0x0a) return 'image/png';
  if (b.length >= 3 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return 'image/jpeg';
  if (b.length >= 6 && (ascii(0, 6) === 'GIF87a' || ascii(0, 6) === 'GIF89a')) return 'image/gif';
  if (b.length >= 12 && ascii(0, 4) === 'RIFF' && ascii(8, 12) === 'WEBP') return 'image/webp';
  if (b.length >= 12 && ascii(4, 8) === 'ftyp' && ['avif', 'avis'].includes(ascii(8, 12))) return 'image/avif';
  return null;
}
